import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Clock, Sparkles } from "lucide-react";
import Footer from "@/components/landing/Footer";
import { useLanguage } from "@/i18n/LanguageContext";

const SuccessPage = () => {
  const { t, language } = useLanguage();
  const homePath = language === "en" ? "/" : `/${language}`;
  const steps = [
    { icon: ShieldCheck, label: t("success.step1") },
    { icon: Sparkles, label: t("success.step2") },
    { icon: Clock, label: t("success.step3") },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-xl"
        >
          <div className="relative rounded-3xl border border-border/60 bg-card/60 backdrop-blur-xl p-8 sm:p-12 text-center overflow-hidden shadow-[var(--shadow-elegant)]">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 pointer-events-none" />

            <div className="relative">
              {/* Spinner */}
              <div className="relative mx-auto mb-8 w-28 h-28 flex items-center justify-center">
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-primary/20"
                  animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0, 0.6] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                  className="absolute inset-2 rounded-full border-2 border-primary/30"
                  animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                />
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                  className="relative"
                >
                  <Loader2 className="w-16 h-16 text-primary" strokeWidth={2.2} />
                </motion.div>
              </div>

              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-3xl sm:text-4xl font-semibold text-foreground tracking-tight leading-[1.1] mb-3"
              >
                {t("success.title")}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="text-base sm:text-lg text-muted-foreground max-w-md mx-auto mb-8"
              >
                {t("success.desc")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8"
              >
                {steps.map((step, i) => (
                  <motion.div
                    key={step.label}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.4 }}
                    className="flex items-center justify-center gap-2 rounded-xl bg-muted/40 border border-border/40 px-3 py-2.5"
                  >
                    <step.icon className="w-4 h-4 text-primary" />
                    <span className="text-xs sm:text-sm font-medium text-foreground/80">{step.label}</span>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="mt-8 pt-6 border-t border-border/40"
              >
                <Link
                  to={homePath}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {t("success.backHome")}
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default SuccessPage;
